import { DateTime } from 'luxon';
import { Source } from '../../enums/enum.source';
import { Entity } from '../entity';
import { InvoiceStatus } from '../invoicing/invoicing.enum';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { ActivityTag } from '../shared/activity';
import { FilterValue } from '../shared/filter';
import { PagingOptions } from '../shared/paging.options';
import { Location } from '../shared/location';
import { BillableType } from '../tenant/work.type';
import { WorkRole } from '../tenant/work.role';
import { FieldWorkOrder } from '../job/field.workorder';
import { TimesheetEntryStatus } from './timesheet.entry.status';

export enum TimesheetEntryState {
    Active = 'Active',
    Paused = 'Paused',
}

export enum TimesheetMode {
    Job = 'Job',
    Tenant = 'Tenant'
}

export enum TimesheetType {
    Normal = 'Normal',
    Travel = 'Travel',
}

export enum TimesheetDateType {
    Week = 'Week',
    Month = 'Month',
    Year = 'Year'
}


export enum DayOfWeek {
    Monday = 'Mon',
    Tuesday = 'Tue',
    Wednesday = 'Wed',
    Thursday = 'Thu',
    Friday = 'Fri',
    Saturday = 'Sat',
    Sunday = 'Sun'
}


export interface TimesheetDuration {
    deductible: number;
    duration: number;
    billable: number;
    customBillable: number;
    capture: number;
}

export interface TimesheetRate {
    cost: number;
    sell: number;
}

export interface TimesheetTotal {
    cost: number;
    amount: number;
}

export interface TimesheetLog {
    time: Date;
    location: Location;
    autoCompleted?: boolean;
}

export interface TimesheetBillingOptions {
    increment: number;
    multiplier: number;
    minimumDuration: number;
}

export interface TimesheetLinkEntity extends Entity {
    type: ActivityEntityName;
}

export interface TimesheetJobViewFilter {
    date?: Date;
    resources: FilterValue[];
    workTypes: FilterValue[];
    workRoles: FilterValue[];
    paging?: PagingOptions;
    parentId?: string;
}

export interface TimesheetJobViewResponse {
    refinenemts: TimesheetJobViewFilter;
    timesheets: TimesheetTable[];
    totalRecords?: number;
}

export interface TimesheetEntry {
    _id: string;
    active: boolean;
    status: TimesheetEntryStatus;
    resource: Entity;
    date: Date;
    workType: Entity,
    workRole: Entity,
    billable: BillableType;
    billingOptions: TimesheetBillingOptions;
    entity: TimesheetLinkEntity;
    links: ActivityTag[];
    source: Source;
    notes: string;
    duration?: TimesheetDuration;
    total?: TimesheetTotal;
    rate?: TimesheetRate;
    start: TimesheetLog;
    end: TimesheetLog;
    updatedAt?: Date;
    updatedBy?: Entity;
    createdAt?: Date;
    createdBy?: Entity;
    actualStart?: TimesheetLog;
    actualEnd?: TimesheetLog;
    timezone: string;
    incompleteState?: TimesheetEntryState;

    /** Invoice ID */
    invoice?: string;

    /** Calculated field */
    invoiceApproved?: boolean;
    /** Calculated field */
    segmentsData?: TimesheetEntry[];

    /** @description null = Normal */
    type?: TimesheetType;
    parentId?: string;
    segments?: string[];
}

export interface TimesheetTable extends Pick<
    TimesheetEntry,
    '_id'
    | 'date'
    | 'resource'
    | 'type'
    | 'segments'
    | 'parentId'
> {
    workType: string;
    workRole: string;
    duration: number;
    billable: number;
    workId: string;
    invoiced: boolean;
    status: TimesheetEntryStatus;
    links: ActivityTag[];
    invoiceApproved: boolean;
    invoice: Entity;
    notes: string;

    children?: TimesheetTable[];
}

export interface TimesheetDayLog {
    value: number;
    review: boolean;
    date: DateTime;
    invoiced: boolean;
}

export interface TimesheetResourceTable {
    _id: string;
    groupPath: string[];
    resource: TimesheetLinkEntity;
    duration: number;
    actual?: number;
    mon: TimesheetDayLog;
    tue: TimesheetDayLog;
    wed: TimesheetDayLog;
    thu: TimesheetDayLog;
    fri: TimesheetDayLog;
    sat: TimesheetDayLog;
    sun: TimesheetDayLog;
    day?: TimesheetDayLog;
}

export interface ResourceTimesheetViewResponse {
    rowData: TimesheetResourceTable[];
    totalTimesheets: number;
    loggedHours: number;
    billableHours: number;
    date: DateTime;
    group: boolean;
}

export interface ResourceTimesheetViewRequest {
    date: DateTime;
    type: TimesheetDateType;
    grouped: boolean;
    onlyRetrieveForUserId?: string;
}

export interface TimesheetExportRequest {
    fromDate: DateTime;
    toDate: DateTime;
    onlyRetrieveForUserId?: string;
}

export interface TimesheetBreakdownRequest {
    resource: Entity;
    date: DateTime;
    workRole?: Entity;
}

export interface FieldTimesheetRequest extends Entity {
    start?: TimesheetLog;
    end?: TimesheetLog;
    resource: Entity;
    entity?: TimesheetLinkEntity;
    workRole?: Entity;
    workType?: Entity;
    source?: Source;
    duration?: TimesheetDuration;
    location?: Location;
    date: Date;
    // IANA timezone the start time was authored in (site/region); lets the field app render elapsed correctly
    timezone?: string;
    notes: string;
    links: ActivityTag[];
    rate: TimesheetRate;
    incompleteState?: TimesheetEntryState;
    deductible?: number;

    // Enable to override the current incomplete timesheet
    override?: boolean;
}


export interface TimesheetResource extends Entity {
    workRole?: WorkRole;
    hourlyCostRate?: number;
    timezone?: string;
}

export const billableOptions = [
    { value: BillableType.Billable, label: BillableType.Billable },
    { value: BillableType.NoCharge, label: BillableType.NoCharge },
    { value: BillableType.NotBillable, label: BillableType.NotBillable },
];

export const timesheetStatus = [
    { _id: TimesheetEntryStatus.Approved, name: TimesheetEntryStatus.Approved },
    { _id: TimesheetEntryStatus.ReviewRequired, name: TimesheetEntryStatus.ReviewRequired }
]

export interface MobileTimesheetEntry extends TimesheetEntry {
    invoiceStatus?: InvoiceStatus
    workOrder?: FieldWorkOrder;
}